import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, getMaxTickLimit, getLabels, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery, Query } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';

import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartEmptyState } from '../ChartEmptyState';
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0px 15px',
            '& .title': {
                fontSize: 16,
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        secondTitleDrilldown: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
            cursor: 'pointer',
            userSelect: 'none',
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
            userSelect: 'none',
        },
        matterSettled: {
            fontSize: '1rem',
            margin: '10px 0px',
            cursor: 'pointer',
            userSelect: 'none',
        },
    }),
);

export enum PeriodEnum {
    WEEK,
    MONTH,
    PREV_MONTH,
    NEXT_MONTH,
    NEXT_6_MONTHS,
    NEXT_12_MONTHS,
    NEXT_3_YEARS,
}

interface FutureFeesChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

interface ChartState {
    last: PeriodEnum;
    totalTimelines: number;
    totalFees: number;
    timelines: number[];
    fees: number[];
    grouping: string[];
    format: string[];
}

export const FutureFeesChart: React.FC<FutureFeesChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const { loading, error, data } = useQuery<FutureFeesData>(FUTURE_SETTLEMENTS_CHART_QYERY, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    const [state, setState] = useState<ChartState>({
        last: PeriodEnum.WEEK,
        totalTimelines: 0,
        totalFees: 0,
        timelines: [],
        fees: [],
        grouping: [],
        format: [],
    });

    useEffect(() => {
        if (!loading) {
            setState((prevState) => {
                let timelines: number[] = [];
                let grouping: string[] = [];
                let fees: number[] = [];
                let totalTimelines: number = 0;
                let totalFees: number = 0;
                let periodString = 'This Week';
                let format: string[] = [];

                if (!error && data && data.statistics && data.statistics.time && data.statistics.time.futureFees) {
                    if (state.last === PeriodEnum.WEEK) {
                        totalTimelines  = data.statistics.time.futureFees.thisWeek.summary.totalTimelines;
                        totalFees       = data.statistics.time.futureFees.thisWeek.summary.totalFees;

                        timelines       = data.statistics.time.futureFees.thisWeek.fees.map((s) => s.timelines);
                        fees            = data.statistics.time.futureFees.thisWeek.fees.map((s) => s.fees);
                        grouping        = data.statistics.time.futureFees.thisWeek.fees.map((s) => s.grouping);
                        format          = data.statistics.time.futureFees.thisWeek.fees.map((s) => s.format);

                        periodString = 'This Week';

                    } else if (state.last === PeriodEnum.PREV_MONTH) {
                        totalTimelines  = data.statistics.time.futureFees.previousMonth.summary.totalTimelines;
                        totalFees       = data.statistics.time.futureFees.previousMonth.summary.totalFees;

                        timelines       = data.statistics.time.futureFees.previousMonth.fees.map((s) => s.timelines);
                        fees            = data.statistics.time.futureFees.previousMonth.fees.map((s) => s.fees);
                        grouping        = data.statistics.time.futureFees.previousMonth.fees.map((s) => s.grouping);
                        format          = data.statistics.time.futureFees.previousMonth.fees.map((s) => s.format);

                        periodString = 'Previous Month';
                    } else if (state.last === PeriodEnum.MONTH) {
                        totalTimelines  = data.statistics.time.futureFees.thisMonth.summary.totalTimelines;
                        totalFees       = data.statistics.time.futureFees.thisMonth.summary.totalFees;

                        timelines       = data.statistics.time.futureFees.thisMonth.fees.map((s) => s.timelines);
                        fees            = data.statistics.time.futureFees.thisMonth.fees.map((s) => s.fees);
                        grouping        = data.statistics.time.futureFees.thisMonth.fees.map((s) => s.grouping);
                        format          = data.statistics.time.futureFees.thisMonth.fees.map((s) => s.format);

                        periodString = 'This Month';
                    } else if (state.last === PeriodEnum.NEXT_MONTH) {
                        totalTimelines  = data.statistics.time.futureFees.nextMonth.summary.totalTimelines;
                        totalFees       = data.statistics.time.futureFees.nextMonth.summary.totalFees;

                        timelines       = data.statistics.time.futureFees.nextMonth.fees.map((s) => s.timelines);
                        fees            = data.statistics.time.futureFees.nextMonth.fees.map((s) => s.fees);
                        grouping        = data.statistics.time.futureFees.nextMonth.fees.map((s) => s.grouping);
                        format          = data.statistics.time.futureFees.nextMonth.fees.map((s) => s.format);

                        periodString = 'Next Month';
                    } else if (state.last === PeriodEnum.NEXT_6_MONTHS) {
                        totalTimelines  = data.statistics.time.futureFees.nextSixMonths.summary.totalTimelines;
                        totalFees       = data.statistics.time.futureFees.nextSixMonths.summary.totalFees;

                        timelines       = data.statistics.time.futureFees.nextSixMonths.fees.map((s) => s.timelines);
                        fees            = data.statistics.time.futureFees.nextSixMonths.fees.map((s) => s.fees);
                        grouping        = data.statistics.time.futureFees.nextSixMonths.fees.map((s) => s.grouping);
                        format          = data.statistics.time.futureFees.nextSixMonths.fees.map((s) => s.format);

                        periodString = 'Next 6 Month';
                    } else if (state.last === PeriodEnum.NEXT_12_MONTHS) {
                        totalTimelines  = data.statistics.time.futureFees.nextTwelveMonths.summary.totalTimelines;
                        totalFees       = data.statistics.time.futureFees.nextTwelveMonths.summary.totalFees;

                        timelines       = data.statistics.time.futureFees.nextTwelveMonths.fees.map((s) => s.timelines);
                        fees            = data.statistics.time.futureFees.nextTwelveMonths.fees.map((s) => s.fees);
                        grouping        = data.statistics.time.futureFees.nextTwelveMonths.fees.map((s) => s.grouping);
                        format          = data.statistics.time.futureFees.nextTwelveMonths.fees.map((s) => s.format);

                        periodString = 'Next 12 Months';
                    } else if (state.last === PeriodEnum.NEXT_3_YEARS) {
                        totalTimelines  = data.statistics.time.futureFees.nextThreeYears.summary.totalTimelines;
                        totalFees       = data.statistics.time.futureFees.nextThreeYears.summary.totalFees;

                        timelines       = data.statistics.time.futureFees.nextThreeYears.fees.map((s) => s.timelines);
                        fees            = data.statistics.time.futureFees.nextThreeYears.fees.map((s) => s.fees);
                        grouping        = data.statistics.time.futureFees.nextThreeYears.fees.map((s) => s.grouping);
                        format          = data.statistics.time.futureFees.nextThreeYears.fees.map((s) => s.format);

                        periodString = 'Next 3 Years';
                    }
                }

                return {
                    ...prevState,
                    totalTimelines,
                    totalFees,
                    timelines,
                    fees,
                    grouping,
                    format,
                    periodString,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [state.last, loading, error, data]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('isFutureSettlementsChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                last: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const handleHeaderDrilldownOnClick = () => () => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            if (state.last === PeriodEnum.WEEK) {
                searchCriterias.push(
                    {
                        property: 'ThisWeek',
                        operator: '',
                        comparison: 'curr',
                        period: '',
                    },
                );
            } else if (state.last === PeriodEnum.PREV_MONTH) {
                searchCriterias.push(
                    {
                        property: 'ThisMonth',
                        operator: '',
                        comparison: 'prev',
                        period: '',
                    },
                );
            } else if (state.last === PeriodEnum.MONTH) {
                searchCriterias.push(
                    {
                        property: 'ThisMonth',
                        operator: '',
                        comparison: 'curr',
                        period: '',
                    },
                );
            } else if (state.last === PeriodEnum.NEXT_MONTH) {
                searchCriterias.push(
                    {
                        property: 'ThisMonth',
                        operator: '',
                        comparison: 'next',
                        period: '',
                    },
                );
            } else if (state.last === PeriodEnum.NEXT_6_MONTHS) {
                searchCriterias.push(
                    {
                        property: '6MonthPeriod',
                        operator: '',
                        comparison: 'next',
                        period: '',
                    },
                );
            } else if (state.last === PeriodEnum.NEXT_12_MONTHS) {
                searchCriterias.push(
                    {
                        property: '12MonthPeriod',
                        operator: '',
                        comparison: 'next',
                        period: '',
                    },
                );
            } else if (state.last === PeriodEnum.NEXT_3_YEARS) {
                searchCriterias.push(
                    {
                        property: 'YearsPeriod',
                        operator: '',
                        comparison: 'next',
                        period: '3',
                    },
                );
            }

            props.getSelectedItem(searchCriterias, true, 'Timesheets', DrillDownType.timesheetSummary, 'Timesheets');
        }
    };
    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };
    
    if (loading) {
        return (
            <ChartSkeleton variant="columns" />
        );
    }
    return (
        <React.Fragment>
            {/* tslint:disable-next-line: no-any */}
            <Query<any> query={CurrencySymbol}>
                {(result) => {
                    if (result.loading) {
                        return (
                            <ChartSkeleton variant="columns" />
                        );
                    }

                    const currencySymbol = result.loading || result.error ? null : result.data.options.currencySymbol;

                    var symbol = currencySymbol ? currencySymbol.symbol : null;
                    return (
                        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.fees) ? ' rv-chart--empty' : ''}`}>
                            <IconButtonWrapper aria-label="close">
                                <ChartInfo 
                                    description={props.description}
                                    fontSize={props.showAllRecords ? 'large' : 'medium'}
                                    className={`${classes.helpIcon} rv-chart-help`}
                                />
                                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                                    <CloseIcon className={classes.closeIcon} />
                                </IconButton>
                            </IconButtonWrapper>
                            <div className={classes.titleContainer}>
                                <ChartHeading icon={<ReceiptOutlinedIcon />} title="Future Fees" />
                                <div className={`${classes.total} h2`} onClick={handleHeaderDrilldownOnClick()}>
                                    <AnimatedNumber value={state.totalFees} format={(amount) => `${symbol}${formatNumber(amount)}`} />
                                </div>
                                <div className={classes.secondTitleDrilldown}>
                                    <span onClick={handleHeaderDrilldownOnClick()}>{`${formatNumber(state.totalTimelines)} ${
                                        state.totalTimelines > 1 ? 'Timelines' : 'Timeline'
                                    }, `}</span>
                                </div>
                            </div>
                            <div className={classes.barContainer}>
                                <Bar
                                    ref={chartRef}
                                    // tslint:disable-next-line: no-any
                                    onClick={(event: React.MouseEvent<any>) => {
                                        const _data = getElementAtEvent(chartRef.current, event);

                                        if (_data.length > 0 && props.getSelectedItem) {
                                            const searchCriterias = new Array<SearchCriteria>();
                                            const index = getElementAtEvent(chartRef.current, event)[0].index;
                                            var selectedGrouping = state.grouping[index];

                                            switch (state.last) {
                                                case PeriodEnum.WEEK:
                                                case PeriodEnum.MONTH:
                                                case PeriodEnum.PREV_MONTH:
                                                case PeriodEnum.NEXT_MONTH:
                                                    searchCriterias.push(
                                                        {
                                                            property: 'ThisDay',
                                                            operator: '',
                                                            comparison: 'day',
                                                            period: selectedGrouping,
                                                        },
                                                    );

                                                    break;
                                                case PeriodEnum.NEXT_6_MONTHS:
                                                case PeriodEnum.NEXT_12_MONTHS:
                                                    searchCriterias.push(
                                                        {
                                                            property: 'ThisMonth',
                                                            operator: '',
                                                            comparison: 'month',
                                                            period: selectedGrouping,
                                                        },
                                                    );

                                                    break;
                                                default:
                                                    searchCriterias.push(
                                                        {
                                                            property: 'YearsPeriod',
                                                            operator: '',
                                                            comparison: 'year',
                                                            period: selectedGrouping,
                                                        },
                                                    );

                                                    break;
                                            }

                                            props.getSelectedItem(searchCriterias, true, 'Timesheets', DrillDownType.timesheetSummary, 'Timesheets');
                                        }
                                    }}
                                    data={{
                                        labels: getLabels(state.format),
                                        
                                        datasets: [
                                            {
                                                fill: false,
                                                label: 'Estimated Fees',
                                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                                data: state.fees,
                                                type: 'bar',
                                            },
                                        // tslint:disable-next-line: no-any
                                        ] as any,
                                    }}
                                    options={{
                                        onHover: onHover,
                                        indexAxis: 'x' as const,
                                        animation: rvChartAnimation(),
                                        elements: { bar: { borderWidth: 0 } },
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        scales: {
                                            y: {
                                                grid: rvValueAxisStyle.grid,
                                                suggestedMax: getSuggestedMaxTick([...state.fees]),
                                                beginAtZero: true,
                                                ticks: {
                                                    ...rvValueAxisStyle.ticks,
                                                    maxTicksLimit: getMaxTickLimit([...state.fees]),
                                                    autoSkip: false,
                                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                                        return `${symbol}${formatNumber(tickValue)}`;
                                                    },
                                                },
                                            },
                                            x: {
                                                grid: rvCategoryAxisStyle.grid,
                                                ticks: {
                                                    ...rvCategoryAxisStyle.ticks,
                                                    autoSkip: false
                                                }
                                            }
                                        },
                                        plugins: {
                                            legend: {
                                                position: 'bottom' as const,
                                                display: false,
                                            },
                                            title: {
                                                display: false,
                                                text: ``,
                                            },
                                            zoom: {
                                                pan: {
                                                    enabled: false,
                                                    onPan: onPan,
                                                },
                                                zoom: {
                                                    wheel: {
                                                        enabled: false,
                                                    },
                                                },
                                            },
                                            tooltip: {
                                                ...rvTooltipStyle,
                                                callbacks: {
                                                    // tslint:disable-next-line: no-any
                                                    label: function (context: any) {
                                                        const {
                                                            formattedValue,
                                                            dataset,
                                                        } = context;

                                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                                        return `${dataset.label}: ${symbol}${formatNumber(currentValue)}`;
                                                    },
                                                    // tslint:disable-next-line: no-any
                                                    afterLabel: (context: any) => {
                                                        const {
                                                              dataIndex,
                                                        } = context;
                                                        const matter = state.timelines[dataIndex];
    
                                                        return [
                                                            `Timelines: ${formatNumber(matter)}`,
                                                        ];
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                />
                                {isChartDataEmpty(state.fees) && (
                                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                                )}
                            </div>
                            <FormControl component="div">
                                <RadioGroup name="last" value={state.last} onChange={onChangeRadio} className={classes.radioGroup}>
                                    <FormControlLabel value={PeriodEnum.WEEK} control={<Radio color="primary" />} label="This Week" />
                                    <FormControlLabel value={PeriodEnum.MONTH} control={<Radio color="primary" />} label="This Month" />
                                    <FormControlLabel value={PeriodEnum.NEXT_MONTH} control={<Radio color="primary" />} label="Next Month" />
                                    <FormControlLabel value={PeriodEnum.PREV_MONTH} control={<Radio color="primary" />} label="Previous Month" />
                                    <FormControlLabel value={PeriodEnum.NEXT_6_MONTHS} control={<Radio color="primary" />} label="Next 6 Months" />
                                    <FormControlLabel value={PeriodEnum.NEXT_12_MONTHS} control={<Radio color="primary" />} label="Next 12 Months" />
                                    <FormControlLabel value={PeriodEnum.NEXT_3_YEARS} control={<Radio color="primary" />} label="Next 3 Years" />
                                </RadioGroup>
                            </FormControl>
                        </div>
                    );
                }}
            </Query>
        </React.Fragment>
    );
};

export interface FutureFeesData {
    statistics: Statistics;
}

export interface Statistics {
    time: Time;
}

export interface Time {
    futureFees: FutureFees;
}

export interface FutureFees {
    thisWeek: FutureFeesTimes;
    thisMonth: FutureFeesTimes;
    previousMonth: FutureFeesTimes;
    nextMonth: FutureFeesTimes;
    nextSixMonths: FutureFeesTimes;
    nextTwelveMonths: FutureFeesTimes;
    nextThreeYears: FutureFeesTimes;
}

export interface FutureFeesTimes {
    fees: FutureFee[];
    summary: Summary;
}

export interface FutureFee {
    timelines: number;
    fees: number;
    grouping: string;
    format: string;
}

export interface Summary {
    totalTimelines: number;
    totalFees: number;
}

const CurrencySymbol = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

const FUTURE_SETTLEMENTS_CHART_QYERY = gql`
    fragment FragmentFutureFeesTimes on FutureFeesTimes {
        fees {
            timelines
            fees
            grouping
            format
        }
        summary {
            totalTimelines
            totalFees
        }
    }

    query FutureFees($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            time {
                futureFees(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    thisWeek {
                        ... FragmentFutureFeesTimes
                    }
                    thisMonth {
                        ... FragmentFutureFeesTimes
                    }
                    previousMonth {
                        ... FragmentFutureFeesTimes
                    }
                    nextMonth {
                        ... FragmentFutureFeesTimes
                    }
                    nextSixMonths {
                        ... FragmentFutureFeesTimes
                    }
                    nextTwelveMonths {
                        ... FragmentFutureFeesTimes
                    }
                    nextThreeYears {
                        ... FragmentFutureFeesTimes
                    }
                }
            }
        }
    }
`;
